import { useEffect, useRef, useState } from 'react';

import { streamLive, streamMyLive } from '../api/live';
import { type ReplayTimeline } from './buildTimeline';

/**
 * Live-session replay engine as a hook (LIVE-WALL). Encapsulates the
 * exact mechanics `SessionReplayDialog` uses for live viewing — open the
 * SSE stream, accumulate NDJSON, rebuild the timeline off-thread in a Web
 * Worker (coalesced), and ease a playhead toward the leading edge — so it
 * can drive many tiles at once on the Live Wall without each tile
 * re-implementing it. The dialog keeps its own copy (it also does VOD +
 * scrubbing); this hook is the follow-live subset.
 *
 * Each active hook = 1 SSE fetch + 1 Web Worker + 1 backend asyncpg
 * connection for its lifetime, so callers gate concurrency (the wall caps
 * visible tiles at 4 and queues the rest).
 */

export type LiveScope =
  | { tenantId: string; sessionId: string }
  | { mine: true; sessionId: string };

export interface LiveReplayState {
  /** Latest rebuilt timeline, or null until the first chunk arrives. */
  timeline: ReplayTimeline | null;
  /** Eased playhead (seconds) tracking the live leading edge. */
  tSec: number;
  /** True once the server signals the session ended (or heartbeat stale). */
  endedLive: boolean;
  /** True once at least one chunk has arrived (stream is producing). */
  connected: boolean;
  /** True once the SSE transport is open, even before any chunk — lets a
   *  tile show "waiting for sensor data" instead of an opaque "connecting"
   *  spinner while the device's sensors warm up (BLE handshake). */
  streaming: boolean;
}

// Follow the leading edge with a small buffer BEHIND it. Chunks arrive in
// bursts (~1–2 s of data at a time), so a playhead that chased the raw edge
// would catch up, stall there, then snap forward on the next chunk — the
// stutter users see. Targeting `edge - LIVE_BUFFER_S` keeps ~1.5 s of runway
// so the playhead eases smoothly at 1x and only ever stalls on a genuine data
// gap. (Adds ~1.5 s of latency to an already-"few-seconds-behind" view.)
const LIVE_BUFFER_S = 1.5;
// If we fall this far behind the buffered target (backgrounded tab, a big
// catch-up batch), snap up to it rather than crawl back at 1x.
const LIVE_MAX_LAG_S = 5;

/**
 * @param live   the session to follow, or null to run idle (no stream).
 * @param enabled gate — when false the stream/worker are torn down (e.g.
 *                a queued tile that isn't visible yet).
 */
export function useLiveReplay(
  live: LiveScope | null,
  enabled: boolean,
): LiveReplayState {
  const [timeline, setTimeline] = useState<ReplayTimeline | null>(null);
  const [tSec, setTSec] = useState(0);
  const [endedLive, setEndedLive] = useState(false);
  const [connected, setConnected] = useState(false);
  const [streaming, setStreaming] = useState(false);

  const accumRef = useRef('');
  const durationRef = useRef(0);

  // Stream + off-thread timeline rebuild. Primitive deps so a fresh `live`
  // object reference from the parent's poll re-render doesn't restart it.
  const active = enabled && live !== null;
  const isMine = live !== null && 'mine' in live;
  const tenantId =
    live !== null && 'tenantId' in live ? live.tenantId : undefined;
  const sessionId = live?.sessionId;
  useEffect(() => {
    if (!active || !sessionId) return;
    let cancelled = false;
    accumRef.current = '';
    durationRef.current = 0;
    setTimeline(null);
    setTSec(0);
    setEndedLive(false);
    setConnected(false);
    setStreaming(false);

    const worker = new Worker(
      new URL('./replay.worker.ts', import.meta.url),
      { type: 'module' },
    );
    let busy = false;
    let pendingText: string | null = null;
    const requestBuild = (text: string) => {
      if (busy) {
        pendingText = text;
        return;
      }
      busy = true;
      worker.postMessage(text);
    };
    worker.onmessage = (
      e: MessageEvent<{ ok: boolean; timeline?: ReplayTimeline }>,
    ) => {
      if (cancelled) return;
      if (e.data.ok && e.data.timeline) {
        setTimeline(e.data.timeline);
        durationRef.current = e.data.timeline.durationS;
      }
      busy = false;
      if (pendingText !== null) {
        const next = pendingText;
        pendingText = null;
        busy = true;
        worker.postMessage(next);
      }
    };

    const handlers = {
      onOpen: () => {
        if (cancelled) return;
        setStreaming(true);
      },
      onChunk: (ndjson: string, _cursor: number) => {
        if (cancelled) return;
        accumRef.current += ndjson;
        setStreaming(true);
        setConnected(true);
        requestBuild(accumRef.current);
      },
      onEnded: () => {
        if (cancelled) return;
        setEndedLive(true);
      },
    };
    const stop = isMine
      ? streamMyLive(sessionId, 0, handlers)
      : streamLive(tenantId as string, sessionId, 0, handlers);
    return () => {
      cancelled = true;
      stop();
      worker.terminate();
    };
  }, [active, isMine, tenantId, sessionId]);

  // Ease the playhead toward the leading edge (read from a ref so the rAF
  // loop runs continuously and doesn't restart on every timeline rebuild).
  useEffect(() => {
    if (!active) return;
    let mounted = true;
    let last: number | undefined;
    let raf: number | undefined;
    const step = (now: number) => {
      if (!mounted) return;
      const dt = (now - (last ?? now)) / 1000;
      last = now;
      const edge = durationRef.current;
      if (edge > 0) {
        // Aim a buffer behind the edge so the playhead always has runway and
        // never stalls at the raw leading edge between chunks.
        const target = Math.max(0, edge - LIVE_BUFFER_S);
        setTSec((prev) => {
          let next = prev + dt;
          if (target - next > LIVE_MAX_LAG_S) next = target;
          return Math.min(next, target);
        });
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      mounted = false;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [active]);

  return { timeline, tSec, endedLive, connected, streaming };
}
